/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import styles from './serviceProviderRegistration.module.scss';
import FormBox from '../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import { Autocomplete, FormHelperText } from '@mui/material';
import ToggleButtonGroup from '../common/ToggleButtonGroup/ToggleButtonGroup';

import UploadImage from 'views/ProviderProfile/components/UploadImage/UploadImage';
import UploadVideo from 'views/ProviderProfile/components/UploadVideo/UploadVideo';
import UploadRetainerAgreement from 'views/ProviderProfile/components/UploadRetainerAgreement/UploadRetainerAgreement';
import BaseInput from 'components/common/BaseInput';

import Chip from '@mui/material/Chip';
import TagFacesIcon from '@mui/icons-material/TagFaces';

import { Theme, useTheme } from '@mui/material/styles';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useCountries, useRegions } from 'hooks/useReferenceData';
import api from 'api';
import stylesAgreement from '../../views/VisaProcess/views/Agreement/Agreement.module.scss';
import PhoneNumberField from 'components/common/PhoneNumberField/PhoneNumberField';
import Modal from '@mui/material/Modal';
import PreviewTemplate from './PreviewTemplate/PreviewTemplate';
import ReactGA from 'react-ga4';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    slotProps: {
        paper: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    },
};

function getStyles(name: string, personName: string[], theme: Theme) {
    return {
        fontWeight:
            personName.indexOf(name) === -1
                ? theme.typography.fontWeightRegular
                : theme.typography.fontWeightMedium,
    };
}
function getSecondaryStyles(name: string, secondaryPersonName: string[], theme: Theme) {
    return {
        fontWeight:
            secondaryPersonName.indexOf(name) === -1
                ? theme.typography.fontWeightRegular
                : theme.typography.fontWeightMedium,
    };
}

interface CompanyForm {
    email: string;
    company_name: string;
    legal_entity_name: string;
    incorporated: boolean;
    country: string;
    region: string;
    address: string;
    city: string;
    zip_code: string;
    vat_number: string;
    phone_number: string;
    short_description: string;
    long_description: string;
}

const initCompanyForm: CompanyForm = {
    email: '',
    company_name: '',
    legal_entity_name: '',
    incorporated: false,
    country: '',
    region: '',
    address: '',
    city: '',
    zip_code: '',
    vat_number: '',
    phone_number: '',
    short_description: '',
    long_description: '',
};

interface CompanyFormMsg {
    email: string;
    company_name: string;
    legal_entity_name: string;
    incorporated: string;
    country: string;
    region: string;
    address: string;
    city: string;
    zip_code: string;
    // vat_number: string;
    phone_number: string;
    short_description: string;
    long_description: string;
    topVisa: string;
}

const initCompanyFormMsg: CompanyFormMsg = {
    email: '',
    company_name: '',
    legal_entity_name: '',
    incorporated: '',
    country: '',
    region: '',
    address: '',
    city: '',
    zip_code: '',
    // vat_number: '',
    phone_number: '',
    short_description: '',
    long_description: '',
    topVisa: '',
};

interface ChipData {
    key: number;
    label: string;
}

interface Expertise {
    expertise_name: string;
    expertise_level: string;
}

interface IPrograms {
    [key: string]: string[];
}

// const ListItem = styled('li')(({ theme }) => ({
//     margin: theme.spacing(0.5),
// }));

export default function Company() {
    const serviceProviderTableName = 'ServiceProvider';
    const expertiseTableName = 'Expertise';
    const [form, setForm] = useState<CompanyForm>(initCompanyForm);
    const [formMsg, setFormMsg] = useState<CompanyFormMsg>(initCompanyFormMsg);
    const [expertiseForm, setExpertiseForm] = useState<Expertise[]>([]);
    const [logo, setLogo] = useState<string | null>(null);
    const [video, setVideo] = useState<string | null>(null);
    const [retainerAgreement, setRetainerAgreement] = useState<string | null>(null);
    const [programs, setPrograms] = useState<IPrograms>({});
    const [expertiseAllOptions, setExpertiseAllOptions] = useState<string[]>(['']);
    const [expertiseOptions, setExpertiseOptions] = useState<string[]>(['']);
    const [incorporated, setIncorporated] = useState<boolean>(false);
    const [countryList, setCountryList] = useState<string[]>(['']);
    const [country, setCountry] = useState<string>('');
    const [templateOpen, setTemplateOpen] = useState<boolean>(false);
    const { data: countryOptions } = useCountries();
    const { data: regionOptions } = useRegions();

    const loadFormData = (data: CompanyForm) => {
        setForm(data);
        setIncorporated(data.incorporated);
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname}`,
            title: 'Company',
        });
    }, []);

    const loadExpertise = (data: Expertise[]) => {
        const main = data.filter((e) => e.expertise_level === 'MAIN');
        let tmp = main.map((val, key) => {
            return { key, label: val.expertise_name };
        });
        setChipData(tmp);

        const secondary = data.filter((e) => e.expertise_level === 'SECONDARY');
        tmp = secondary.map((val, key) => {
            return { key, label: val.expertise_name };
        });
        setSecondaryChipData(tmp);
        setExpertiseForm(data);
    };

    useEffect(() => {
        api.tableInput.newFetchCandidateInput(loadFormData, serviceProviderTableName);
        api.tableInput.newFetchCandidateInput(loadExpertise, expertiseTableName);
    }, []);

    useEffect(() => {
        (async () => {
            const resExp = await api.staticResources.getExpertiseList();
            if (resExp.status === 200) {
                const tmpPrograms: { [key: string]: any } = resExp.data;
                setPrograms(tmpPrograms);
                const countries = Object.keys(tmpPrograms);
                const defaultCountry = countries[0];
                setCountryList(countries);
                setCountry(defaultCountry);
                setExpertiseAllOptions(tmpPrograms[defaultCountry]);
                setExpertiseOptions(tmpPrograms[defaultCountry]);
            }
        })();
    }, []);

    const handleCountryChange = (newCountry: string) => {
        setCountry(newCountry);
        setExpertiseAllOptions(programs[newCountry]);
        resetExpertiseOptions(newCountry);
    };

    useEffect(() => {
        setTimeout(() => {
            (async () => {
                const resPic = await api.resource.get('logo');
                if (resPic.status === 200 || resPic.status === 201) {
                    const url = resPic.data['url'];
                    setLogo(url);
                }
            })();
        }, 300);
    }, []);

    useEffect(() => {
        setTimeout(() => {
            (async () => {
                const resVid = await api.resource.get('video');
                if (resVid.status === 200 || resVid.status === 201) {
                    const url = resVid.data['url'];
                    setVideo(url);
                }
            })();
        }, 300);
    }, []);

    useEffect(() => {
        setTimeout(() => {
            (async () => {
                const res = await api.resource.get('retainer_agreement');
                if (res.status === 200 || res.status === 201) {
                    const url = res.data['url'];
                    setRetainerAgreement(url);
                }
            })();
        }, 300);
    }, []);

    // FIELD VALIDATION
    const validate = (field: string, value: string) => {
        let validated = true;
        let tmp = formMsg;
        if (!field && !value) {
            if (!form.incorporated && !Object.prototype.hasOwnProperty.call(form, 'company_name')) {
                const tempo = form;
                tempo['company_name'] = '';
                setForm(tempo);
            }
            for (const [key, value] of Object.entries(form)) {
                let msg = '';
                if (key === 'company_name') {
                    if (value === '' && incorporated) {
                        msg = 'Field should not be left empty';
                        validated = false;
                        tmp = { ...tmp, [key]: msg };
                        setFormMsg(tmp);
                    }
                } else if (key === 'vat_number') {
                    continue;
                } else if (value === '') {
                    msg = 'Field should not be left empty';
                    validated = false;
                    tmp = { ...tmp, [key]: msg };
                    setFormMsg(tmp);
                } else if (key === 'email') {
                    if (!validateEmail(key, value)) {
                        msg = 'Invalid email address';
                        tmp = { ...tmp, [key]: msg };
                        setFormMsg(tmp);
                        validated = false;
                    }
                }
            }
        } else {
            let msg = '';
            if (value === '') {
                msg = 'Field should not be left empty';
                validated = false;
            }
            tmp = { ...tmp, [field]: msg };
        }
        setFormMsg(tmp);

        if (!field && !value) {
            for (const [key] of Object.entries(formMsg)) {
                let msg = '';
                if (key === 'topVisa' && !chipData.length) {
                    msg = 'Field should not be left empty';
                    validated = false;
                }
                tmp = { ...tmp, topVisa: msg };
            }
        } else {
            let msg = '';
            if (field === 'topVisa' && !chipData.length) {
                msg = 'Field should not be left empty';
                validated = false;
            }
            tmp = { ...tmp, topVisa: msg };
        }
        setFormMsg(tmp);

        return validated;
    };

    const handleUpdate = (field: string, value: string) => {
        validate(field, value);
        // if (field === 'short_description' && value.length > 170) return;
        // if (field === 'long_description' && value.length > 500) return;
        const tmp: CompanyForm = { ...form, [field]: value };
        setForm(tmp);
    };

    const [chipData, setChipData] = useState<ChipData[]>([]);
    const [secondaryChipData, setSecondaryChipData] = useState<ChipData[]>([]);

    const handleDelete = (chipToDelete: ChipData) => () => {
        setChipData((chips) => chips.filter((chip) => chip.key !== chipToDelete.key));
        setPersonName((expertise) => expertise.filter((name) => name !== chipToDelete.label));
        let temp = [...expertiseForm];
        temp = temp.filter(
            (item) =>
                item.expertise_name !== chipToDelete.label || item.expertise_level === 'SECONDARY'
        );
        setExpertiseForm(temp);
    };

    const handleDeleteSecondary = (chipToDelete: ChipData) => () => {
        setSecondaryChipData((chips) => chips.filter((chip) => chip.key !== chipToDelete.key));
        setSecondaryPersonName((expertise) =>
            expertise.filter((name) => name !== chipToDelete.label)
        );
        let temp = [...expertiseForm];
        temp = temp.filter(
            (item) => item.expertise_name !== chipToDelete.label || item.expertise_level === 'MAIN'
        );
        setExpertiseForm(temp);
    };

    const theme = useTheme();
    const [personName, setPersonName] = useState<string[]>([]);
    const [secondaryPersonName, setSecondaryPersonName] = useState<string[]>([]);

    const resetExpertiseOptions = (country?: string) => {
        // prevents expertise duplication
        const selectedMain = chipData.map((item) => item.label);
        const selectedSecondary = secondaryChipData.map((item) => item.label);
        const selected = selectedMain.concat(selectedSecondary);
        let tmpExpertiseOptions;
        if (country) {
            tmpExpertiseOptions = programs[country].filter((item) => !selected.includes(item));
        } else {
            tmpExpertiseOptions = expertiseAllOptions.filter((item) => !selected.includes(item));
        }
        setExpertiseOptions(tmpExpertiseOptions);
    };

    useEffect(() => {
        resetExpertiseOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personName, secondaryPersonName, secondaryChipData, chipData]);

    const handleChange = (event: SelectChangeEvent<typeof personName>) => {
        const {
            target: { value },
        } = event;

        const x = chipData;
        const val = value[value.length - 1];
        setPersonName(typeof value === 'string' ? value.split(',') : value);

        // prevents adding more than 5 expertise items - otherwise possible if dropdown not closed between two additions
        if (chipData.length >= 5) return;
        x.push({ key: x.length, label: val });
        setChipData(chipData);
        const temp = [...expertiseForm];
        temp.push({ expertise_name: val, expertise_level: 'MAIN' });
        setExpertiseForm(temp);

        validate('topVisa', val);
    };

    const handleSecondaryChange = (event: SelectChangeEvent<typeof secondaryPersonName>) => {
        const {
            target: { value },
        } = event;

        const x = secondaryChipData;

        const val = value[value.length - 1];

        setSecondaryPersonName(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value
        );

        if (secondaryChipData.length >= 5) return;
        x.push({ key: x.length, label: val });
        setSecondaryChipData(secondaryChipData);
        const temp = [...expertiseForm];
        temp.push({ expertise_name: val, expertise_level: 'SECONDARY' });
        setExpertiseForm(temp);
    };

    const handleLoadProviderLogo = async (imgFile: File) => {
        const formData: FormData = new FormData();
        formData.append('logo', imgFile);
        await api.resource.post('logo', formData);
    };

    const handleLoadProviderVideo = async (videoFile: File) => {
        const formData: FormData = new FormData();
        formData.append('video', videoFile);
        await api.resource.post('video', formData);
    };

    const handleLoadRetainerAgreement = async (textFile: File) => {
        const formData: FormData = new FormData();
        formData.append('retainer_agreement', textFile);
        await api.resource.post('retainer_agreement', formData);
    };

    const validateEmail = (field: string, value: string) => {
        handleUpdate(field, value);
        let validated = true;
        let msg = '';
        let tmp = formMsg;

        if (!value) {
            msg = 'Field should not be left empty';
            validated = false;
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
            msg = 'Invalid email address';
            validated = false;
        }
        tmp = { ...tmp, [field]: msg };

        setFormMsg(tmp);
        return validated;
    };

    const handleIncorporated = (value: string) => {
        if (value === 'Yes') {
            const tmp: CompanyForm = { ...form, incorporated: true };
            setForm(tmp);
            setIncorporated(true);
        } else if (value === 'No') {
            const tmp: CompanyForm = { ...form, incorporated: false };
            tmp.company_name = '';
            setForm(tmp);
            setIncorporated(false);
        }
    };

    return (
        <FormBox
            validation={validate}
            data={[form, expertiseForm]}
            tableNames={[serviceProviderTableName, expertiseTableName]}
        >
            <p className="form-card-title">Company</p>
            <p className={`mb-6 mt-4`}>
                * Please make sure to fill all the fields accureately. Except for your phone number
                and email, the business information you provide will be filled automatically into
                your retainer agreement with the clients.
            </p>
            <span className="mb-8 mt-8">
                <ToggleButtonGroup
                    options={['No', 'Yes']}
                    name=""
                    label={'Is your company incorporated?'}
                    value={form.incorporated ? 'Yes' : 'No'}
                    onChange={(_: string, value: string) => handleIncorporated(value)}
                />
            </span>
            <div className={`grid-section d-grid-2 mb-8`}>
                <span className="flex flex-col gap-5">
                    <BaseInput
                        sx={{
                            width: 356,
                        }}
                        id="legal_entity_name"
                        label="Legal entity name"
                        type="text"
                        value={form.legal_entity_name}
                        error={formMsg.legal_entity_name !== ''}
                        onFocus={(e) => {
                            validate('legal_entity_name', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('legal_entity_name', e.target.value);
                        }}
                        helperText={formMsg.legal_entity_name}
                    />
                    <BaseInput
                        sx={{
                            width: 356,
                        }}
                        disabled={!incorporated}
                        id="company_name"
                        label="Company name shown to clients"
                        type="text"
                        value={form.company_name}
                        error={formMsg.company_name !== '' && incorporated}
                        onFocus={(e) => {
                            validate('company_name', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('company_name', e.target.value);
                        }}
                        helperText={formMsg.company_name}
                    />
                </span>
                <div className={`flex items-center gap-x-2.5`}>
                    <UploadImage src={logo ? logo : ''} getImageFile={handleLoadProviderLogo} />
                    <div>
                        <p>Company Logo</p>
                        <p className={`${styles.boldPar}`}>Upload company logo</p>
                    </div>
                </div>
            </div>
            <p className={`form-card-content-title`}>Select country you operate in</p>
            <p>
                Select country and then select visa programs that you specialize in from that
                country:
            </p>

            <div className={`grid-section d-grid-2 `}></div>

            <div>
                <FormControl sx={{ m: 1, width: 300 }}>
                    <InputLabel id="demo-multiple-name-label">Country</InputLabel>
                    <Select
                        labelId="demo-multiple-name-label"
                        id="demo-multiple-name"
                        value={country}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        input={<OutlinedInput label="Name" />}
                        MenuProps={MenuProps}
                        sx={{
                            width: 356,
                        }}
                    >
                        {countryList.map((name, i) => (
                            <MenuItem key={i} value={name}>
                                {name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            <div className={`mb-14`}>
                <p className={`form-card-content-title`}>List your 5 preferred visas</p>
                <p>Insert the 5 visa programs you or your company are most specialized in:</p>

                <div className={`grid-section d-grid-2 `}></div>

                <div>
                    <FormControl sx={{ m: 1, width: 300 }}>
                        <InputLabel id="demo-multiple-name-label">Visa</InputLabel>

                        <Select
                            labelId="demo-multiple-name-label"
                            id="demo-multiple-name"
                            multiple
                            value={personName}
                            onFocus={(e) => {
                                validate('topVisa', e.target.value);
                            }}
                            onChange={handleChange}
                            input={<OutlinedInput label="Name" />}
                            MenuProps={MenuProps}
                            disabled={chipData.length >= 5}
                            sx={{
                                width: 356,
                            }}
                            error={formMsg.topVisa !== ''}
                        >
                            {expertiseOptions.map((name, i) => (
                                <MenuItem
                                    key={i}
                                    value={name}
                                    style={getStyles(name, personName, theme)}
                                >
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            <span style={{ color: '#d32f2f' }}>{formMsg.topVisa}</span>
                        </FormHelperText>
                    </FormControl>
                </div>

                {/* <Paper
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        listStyle: 'none',
        p: 0.5,
        m: 0,
        // boxShadow: none,
      }}
      component="ul"
    > */}
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                    {chipData.map((data) => {
                        let icon;

                        if (data.label === 'React') {
                            icon = <TagFacesIcon />;
                        }

                        return (
                            // <ListItem key={data.key}>
                            <div key={data.key} style={{ marginLeft: 10, marginTop: 10 }}>
                                <Chip
                                    icon={icon}
                                    label={data.label}
                                    onDelete={
                                        data.label === 'React' ? undefined : handleDelete(data)
                                    }
                                />
                            </div>
                            // </ListItem>
                        );
                    })}
                    {/* </Paper> */}
                </div>
            </div>

            <div className={`mb-14`}>
                <p className={`form-card-content-title`}>List additional 5 visa programs</p>
                <p>
                    Insert 5 additional visas programs that you or your company are comfortable
                    working with:
                </p>

                <div className={`mt-8`}>
                    <FormControl sx={{ m: 1, width: 300 }}>
                        <InputLabel id="demo-multiple-name-label">Visa</InputLabel>
                        <Select
                            labelId="demo-multiple-name-label"
                            id="demo-multiple-name"
                            multiple
                            value={secondaryPersonName}
                            onChange={handleSecondaryChange}
                            input={<OutlinedInput label="Name" />}
                            MenuProps={MenuProps}
                            disabled={secondaryChipData.length >= 5}
                            sx={{
                                width: 356,
                            }}
                        >
                            {expertiseOptions.map((name, j) => (
                                <MenuItem
                                    key={j}
                                    value={name}
                                    style={getSecondaryStyles(name, secondaryPersonName, theme)}
                                >
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                {/* <Paper
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        listStyle: 'none',
        p: 0.5,
        m: 0,
        // boxShadow: none,
      }}
      component="ul"
    > */}
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                    {secondaryChipData.map((data) => {
                        let icon;

                        if (data.label === 'React') {
                            icon = <TagFacesIcon />;
                        }

                        return (
                            // <ListItem key={data.key}>
                            <div key={data.key} style={{ marginLeft: 10, marginTop: 10 }}>
                                <Chip
                                    icon={icon}
                                    label={data.label}
                                    onDelete={
                                        data.label === 'React'
                                            ? undefined
                                            : handleDeleteSecondary(data)
                                    }
                                />
                            </div>
                            // </ListItem>
                        );
                    })}
                </div>
                {/* </Paper> */}
            </div>

            <div className={`mb-14`}>
                <p className={`form-card-content-title`}>Company description</p>
                <p className={`mb-6`}>
                    Please describe your company in up to 170 characters. The description will be
                    displayed to potential clients:
                </p>

                <BaseInput
                    sx={{
                        '&.Mui-error': {
                            '.MuiOutlinedInput-notchedOutline ': {
                                borderColor: 'blue',
                            },
                        },
                        marginBottom: 4,
                    }}
                    multiline={true}
                    rows={2}
                    id="short_description"
                    label="Short Description"
                    type="text"
                    value={form.short_description}
                    error={formMsg.short_description !== ''}
                    onFocus={(e) => {
                        validate('short_description', e.target.value);
                    }}
                    onChange={(e) => {
                        handleUpdate('short_description', e.target.value);
                    }}
                    helperText={formMsg.short_description}
                    fullWidth
                    inputProps={{ maxLength: 170 }}
                />
                {form.short_description && form.short_description !== '' && (
                    <p className={`flex justify-end mb-6 text-sm`} style={{ marginTop: '-30px' }}>
                        Remaining text: {170 - form.short_description.length}
                    </p>
                )}
                <p className={`mb-6`}>Please describe your company in 500 characters:</p>
                <BaseInput
                    sx={{
                        '&.Mui-error': {
                            '.MuiOutlinedInput-notchedOutline ': {
                                borderColor: 'blue',
                            },
                        },
                        marginBottom: 4,
                    }}
                    multiline={true}
                    rows={3}
                    id="longtDesc"
                    label="Long Description"
                    type="text"
                    value={form.long_description}
                    error={formMsg.long_description !== ''}
                    onFocus={(e) => {
                        validate('long_description', e.target.value);
                    }}
                    onChange={(e) => {
                        handleUpdate('long_description', e.target.value);
                    }}
                    helperText={formMsg.long_description}
                    fullWidth
                    inputProps={{ maxLength: 500 }}
                />
                {form.long_description && form.long_description !== '' && (
                    <p className={`flex justify-end mb-6 text-sm`} style={{ marginTop: -30 }}>
                        Remaining text: {500 - form.long_description.length}
                    </p>
                )}
                <div className={`${styles.gridSection} grid-section d-grid-2`}>
                    <BaseInput
                        sx={{ marginBottom: 4 }}
                        id="email"
                        label="Email registered with regulatory body"
                        type="text"
                        value={form.email}
                        error={formMsg.email !== ''}
                        onFocus={(e) => {
                            validate('email', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('email', e.target.value);
                        }}
                        helperText={formMsg.email}
                    />

                    <PhoneNumberField
                        sx={{ marginBottom: 4 }}
                        label="Phone number"
                        fullWidth
                        value={form.phone_number ? form.phone_number : ''}
                        onFocus={(e) => {
                            validate('phone_number', e.target.value);
                        }}
                        onChange={(v: any) => {
                            handleUpdate('phone_number', v);
                        }}
                        error={formMsg.phone_number !== ''}
                        helperText={formMsg.phone_number}
                    />

                    <Autocomplete
                        sx={{
                            '& .MuiInputBase-input': {
                                height: 30,
                            },
                            '&.Mui-error': {
                                '.MuiOutlinedInput-notchedOutline ': {
                                    borderColor: '#ff2727',
                                },
                            },
                        }}
                        disablePortal
                        value={form.country}
                        id="residence"
                        options={countryOptions ?? []}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            validate('country', e.target.value);
                        }}
                        onChange={(_, value: any) => handleUpdate('country', value)}
                        renderInput={(params) => (
                            <BaseInput
                                sx={{
                                    '&.Mui-error': {
                                        '.MuiOutlinedInput-notchedOutline ': {
                                            borderColor: 'blue',
                                        },
                                    },
                                    marginBottom: 4,
                                }}
                                {...params}
                                label="Country"
                                helperText={formMsg.country}
                                error={formMsg.country !== ''}
                            />
                        )}
                    />
                    <Autocomplete
                        sx={{
                            '&.Mui-error': {
                                '.MuiOutlinedInput-notchedOutline ': {
                                    borderColor: 'blue',
                                },
                            },
                            '& .MuiInputBase-input': {
                                height: 30,
                            },
                        }}
                        disablePortal
                        id="region"
                        value={form.region}
                        options={regionOptions?.[form.country] ?? []}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            validate('region', e.target.value);
                        }}
                        onChange={(_, v: any) => handleUpdate('region', v)}
                        renderInput={(params) => (
                            <BaseInput
                                sx={{
                                    '&.Mui-error': {
                                        '.MuiOutlinedInput-notchedOutline ': {
                                            borderColor: 'blue',
                                        },
                                    },
                                    marginBottom: 4,
                                }}
                                {...params}
                                label="region"
                                error={formMsg.region !== ''}
                                helperText={formMsg.region}
                            />
                        )}
                    />

                    <BaseInput
                        sx={{ marginBottom: 4 }}
                        id="city"
                        label="City"
                        type="text"
                        value={form.city}
                        error={formMsg.city !== ''}
                        onFocus={(e) => {
                            validate('city', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('city', e.target.value);
                        }}
                        helperText={formMsg.city}
                    />

                    <BaseInput
                        sx={{ marginBottom: 4 }}
                        id="address"
                        label="Address"
                        type="text"
                        value={form.address}
                        error={formMsg.address !== ''}
                        onFocus={(e) => {
                            validate('address', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('address', e.target.value);
                        }}
                        helperText={formMsg.address}
                    />

                    <BaseInput
                        sx={{ marginBottom: 4 }}
                        id="zip_code"
                        label="Zip code"
                        type="text"
                        value={form.zip_code}
                        error={formMsg.zip_code !== ''}
                        onFocus={(e) => {
                            validate('zip_code', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('zip_code', e.target.value);
                        }}
                        helperText={formMsg.zip_code}
                    />

                    <BaseInput
                        sx={{ marginBottom: 4 }}
                        id="vat_number"
                        label="Vat number"
                        type="text"
                        value={form.vat_number}
                        // error={formMsg.vat_number !== ''}
                        // onFocus={(e) => {
                        //     validate('vat_number', e.target.value);
                        // }}
                        onChange={(e) => {
                            handleUpdate('vat_number', e.target.value);
                        }}
                        // helperText={formMsg.vat_number}
                    />
                </div>
            </div>

            <div className={`mb-14`}>
                <p className={`form-card-content-title`}>Company video</p>
                <p className={`mb-6`}>
                    We recommend uploading a short video presenting your company, yourself & the
                    services you provide. This will significantly increase your conversion rates:
                </p>
                <UploadVideo src={video ? video : ''} getImageFile={handleLoadProviderVideo} />
                {/* <p className={`${styles.boldPar}`}>
                    How to create your video? Watch our guidelines:
                </p> */}
            </div>
            <div className={`mb-14`}>
                <p className={`form-card-content-title`}>Template retainer agreement</p>
                <p className={`mb-1`}>
                    Upload a template of your company's standard contract or use one of our
                    templates.
                </p>
                <p
                    className={`mb-3`}
                    style={{
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        color: '#4A52BB',
                        textDecorationLine: 'underline',
                    }}
                    onClick={() => setTemplateOpen(true)}
                >
                    Click here to view our standard agreement
                </p>
                <UploadRetainerAgreement
                    src={retainerAgreement ? retainerAgreement : ''}
                    getFile={handleLoadRetainerAgreement}
                />
                <div className={styles['document-preview']}>
                    <Modal
                        sx={{ zIndex: '10000' }}
                        open={templateOpen}
                        onClose={() => setTemplateOpen(false)}
                    >
                        <div className={stylesAgreement['modal-contract-wrapper']}>
                            <div className={stylesAgreement['modal-contract']}>
                                <PreviewTemplate closePreview={() => setTemplateOpen(false)} />
                            </div>
                        </div>
                    </Modal>
                </div>
            </div>
        </FormBox>
    );
}
